// import {Form, FormEdit, FormGrid, Grid} from '@formio/react';
import React, { useCallback, useEffect, useState } from "react"
import { addDoc, collection, doc, onSnapshot } from "firebase/firestore"
import { auth, db, functions } from "../../firebase"
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth"

import { DataTable } from "../core/CreateEditOptions/DataTable"
import { PopupService } from "react-ui-components/components/PopupService"
import { UsersCountResetActionView } from "react-ui-components/components/admin/UsersCountResetActionView"
// import FormGroup from "@mui/material/FormGroup"
// import Popup from "reactjs-popup"
import add from "../core/CreateEditOptions/add"
import { getColumns } from "./getColumns"
import { getData } from "../core/CreateEditOptions/getData"
import getMap from "./getMap"
import { httpsCallable } from "firebase/functions"
import { options } from "./options"
import { schema } from "./schema"
// import update from "../core/CreateEditOptions/update"
import { updateData } from "../core/CreateEditOptions/updateData"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

export const updateUser = httpsCallable(functions, "updateUser")
export const resetUsersStats = httpsCallable(functions, "resetUsersStats")

export const PATH = "users"
const BUCKET = "users/assets"
const TITLE = "Users Config"
const ADD_TITLE = "Add new User"
const IMAGE_PROP_NAME = "profileImageUrl"

const Main = () => {
  const [data, setData] = useState<any[]>([])
  const [loading, setLoading] = useState<any>()
  const [columns, setColumns] = useState<any>([])
  const [resetLoading, setResetLoading] = useState(false)
  const [resetError, setResetError] = useState("")
  const [currentUsersCount, setCurrentUsersCount] = useState<number | null>(null)
  const [lastResetCount, setLastResetCount] = useState<number | null>(null)
  const [selectedRowData, setSelectedRowData] = useState<any>(null)

  const navigate = useNavigate()
  const { t } = useTranslation()

  const filterFunction = useCallback((data: { email: any; name: any }) => {
    return !data?.email && !data?.name
  }, [])

  const reloadUsers = useCallback(() => {
    getData({ setData, path: PATH, getMap, isCollection: true, setLoading, filterFunction })
  }, [filterFunction])

  useEffect(() => {
    reloadUsers()
  }, [reloadUsers])

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "app-configurations", "usersStats"), (snapshot) => {
      const count = snapshot.data()?.count
      setCurrentUsersCount(typeof count === "number" ? count : 0)
    })

    return unsubscribe
  }, [])

  const handleResetUsersCount = useCallback(() => {
    PopupService.setConfirm(
      t(
        "usersCountReset.confirmMessage",
        "Reset the public users count from the current registered users?",
      ),
      (result?: string) => {
        if (result !== "OK") {
          return
        }

        void (async () => {
          setResetLoading(true)
          setResetError("")

          try {
            const response = await resetUsersStats({})
            const count = Number((response.data as { count?: number })?.count ?? 0)
            setCurrentUsersCount(count)
            setLastResetCount(count)
            PopupService.pushPopup(
              t("usersCountReset.successMessage", {
                count,
                defaultValue: "Users count reset to {{count}} registered users.",
              }),
            )
          } catch (error: any) {
            const message = t(
              "usersCountReset.errorMessage",
              "Unable to reset users count. Please try again.",
            )
            setResetError(error?.message ? `${message} ${error.message}` : message)
            PopupService.pushPopup(error?.message ? `${message} ${error.message}` : message)
          } finally {
            setResetLoading(false)
          }
        })()
      },
    )
  }, [t])

  useEffect(() => {
    if (data?.length) {
      const resultColumns = getColumns({
        imagePropName: IMAGE_PROP_NAME,
        data,
        setLoading,
        setData,
        path: PATH,
        onEditSuccess: async ({ result, close, id }: any) => {
          await updateData({
            path: `${PATH}/${id}`,
            setLoading,
            data: result.formData,
          } as any)

          reloadUsers()
          close()
        },
      } as any)
      setColumns(resultColumns)
    }
  }, [data, filterFunction, reloadUsers])

  return (
    <div>
      <UsersCountResetActionView
        title={t("usersCountReset.title", "Users counter")}
        description={t(
          "usersCountReset.description",
          "Recalculate the public users count from current registered user profiles.",
        )}
        buttonLabel={t("usersCountReset.buttonLabel", "Reset users count")}
        loadingLabel={t("usersCountReset.loadingLabel", "Resetting...")}
        isLoading={resetLoading}
        currentCount={currentUsersCount}
        currentCountLabel={
          typeof currentUsersCount === "number"
            ? t("usersCountReset.currentCountLabel", {
                count: currentUsersCount,
                defaultValue: "Current count: {{count}}",
              })
            : undefined
        }
        lastCount={lastResetCount}
        lastCountLabel={
          typeof lastResetCount === "number"
            ? t("usersCountReset.lastCountLabel", {
                count: lastResetCount,
                defaultValue: "Last reset count: {{count}}",
              })
            : undefined
        }
        errorMessage={resetError}
        onReset={handleResetUsersCount}
      />
      <DataTable
        isCollection
        imagePropName={IMAGE_PROP_NAME}
        path={PATH}
        bucket={BUCKET}
        title={TITLE}
        addTitle={ADD_TITLE}
        setLoading={setLoading}
        setData={setData}
        getMap={getMap}
        loading={loading}
        data={data}
        columns={columns}
        options={options}
        schema={schema}
        onRowClicked={(rowData: any, rowMeta: { dataIndex: string | number }) => {
          const d = data?.[rowMeta.dataIndex]
          navigate(`/users/${d.id}`)
          // console.log("selectedRowData------->", d)
          // setSelectedRowData(d)
          // PopupService.setPopup(
          //   <UserDetail
          //     selectedRowData={d}
          //     setLoading={setLoading}
          //     setSelectedRowData={setSelectedRowData}
          //   />,
          // )
          // setOpenDetails(true);
        }} //rowData: string[], rowMeta: { dataIndex: number; rowIndex: number }
        onSuccess={
          (() => {
            reloadUsers()
          }) as any
        }
        onEditSuccess={() => {
          reloadUsers()
        }}
        onAddSuccess={async ({ result, close }: any) => {
          await add({
            path: PATH,
            setLoading,
            data: result.formData,
          } as any)

          reloadUsers()
          close()
        }}
      />

      {/* <Popup
      title="User Details"
      open={openDetails}
      onClose={setOpenDetails}
    >
      {close => {
        if (!selectedRowData) {
          return null;
        }
        return <UserDetail close={close} selectedRowData={selectedRowData} setLoading={setLoading} setSelectedRowData={setSelectedRowData} />;
      }}
    </Popup> */}
    </div>
  )
}

export function insertSpaces(str: string) {
  return str
    .replace(/(_|-)/g, " ")
    .trim()
    .replace(/\w\S*/g, function (str: string) {
      return str.charAt(0).toUpperCase() + str.substr(1)
    })
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
  // string = string.replace(/([a-z])([A-Z])/g, '$1 $2');
  // string = string.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
  // return string;
}
export const registerWithEmailAndPassword = async (name: any, email: string, password: string) => {
  try {
    const res = await createUserWithEmailAndPassword(auth, email, password)
    const user = res.user
    await addDoc(collection(db, "users"), {
      uid: user.uid,
      name,
      authProvider: "local",
      email,
    })
  } catch (err: any) {
    console.error(err)
    alert(err.message)
  }
}

export const sendPasswordReset = async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email)
    alert("Password reset link sent!")
  } catch (err: any) {
    console.error(err)
    alert(err.message)
  }
}

export const _sendEmailVerification = async (user: any) => {
  try {
    auth.currentUser && (await sendEmailVerification(auth.currentUser))
    alert("Email verification link sent!")
  } catch (err: any) {
    console.error(err)
    alert(err.message)
  }
}

export const logout = () => {
  signOut(auth)
}

export default Main
